/**
 * Artifacts (f15.2): every artifact the caller may see, whatever node it hangs
 * off. Mirrors the prototype's `artifacts.html`.
 *
 * Visibility is NOT decided here: `GET /api/artifacts` already returns exactly
 * the artifacts whose owning node is in the caller's scope (BR-F15.2-01/02),
 * and the count in the subtitle is the length of that answer (BR-F15.2-06).
 * The search box, the two selects and the pager run over that collection in
 * the browser and make no request (Q-f15.2-4).
 *
 * "New artifact" appears only when some level has owner options, and the
 * dialog asks for the owning node before it accepts a file (BR-F15.2-04).
 *
 * The Codes column (f15.4) counts each artifact's effectively Active access
 * codes. Not built here (Q-f15.2-2): the prototype's Visibility column, the
 * visibility filter and "Access report" -- f15.5 and f15.6 add them.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowRight, Briefcase, Building2, ChevronLeft, ChevronRight, FileText, GitBranch, Network, Plus, Search, SearchX } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { ArtifactListItem, ArtifactOwnerType } from '@/api/client';
import { useArtifactOwnerOptions, useArtifacts } from '@/api/queries';

import {
  NO_FILTERS,
  artifactPageOf,
  filterArtifacts,
  formatDate,
  hangsOffPath,
  levelLabel,
  typeLabel,
  type ArtifactFilters,
  type LevelFilter,
  type StateFilter,
} from './artifactView';
import { NewArtifactDialog } from './NewArtifactDialog';

const LEVELS: readonly ArtifactOwnerType[] = ['BUSINESS_UNIT_GROUP', 'BUSINESS_UNIT', 'CLIENT', 'ENGAGEMENT'];

const LEVEL_ICONS: Record<string, typeof Briefcase> = {
  BUSINESS_UNIT_GROUP: Building2,
  BUSINESS_UNIT: Network,
  CLIENT: Briefcase,
  ENGAGEMENT: FileText,
};

/** The levels on which the caller may publish, from the server's owner options. */
function usePublishableLevels(): ArtifactOwnerType[] {
  const group = useArtifactOwnerOptions('BUSINESS_UNIT_GROUP');
  const unit = useArtifactOwnerOptions('BUSINESS_UNIT');
  const client = useArtifactOwnerOptions('CLIENT');
  const engagement = useArtifactOwnerOptions('ENGAGEMENT');
  const answers = [group, unit, client, engagement];
  return LEVELS.filter((_level, index) => (answers[index]?.data?.items.length ?? 0) > 0);
}

function PageHead({ count, onNew }: { count: number | null; onNew: (() => void) | null }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Artifacts</h1>
        <div className="page-sub">
          Published documents across the hierarchy
          {count !== null && <> &middot; {count} you may see</>}
        </div>
      </div>
      <div className="page-actions">
        {onNew && (
          <button type="button" className="btn btn-primary" onClick={onNew}>
            <Plus aria-hidden="true" /> New artifact
          </button>
        )}
      </div>
    </div>
  );
}

function HangsOff({ owner }: { owner: ArtifactListItem['owner'] }) {
  const Icon = LEVEL_ICONS[owner.type] ?? FileText;
  const path = hangsOffPath(owner);
  const name = owner.parent ? `${owner.name} · ${owner.parent.name}` : owner.name;
  return (
    <>
      <span className="badge badge-chip">
        <Icon aria-hidden="true" /> {levelLabel(owner.type)}
      </span>
      <span className="sub">
        {path ? (
          <Link className="cell-link" to={path}>
            {name}
          </Link>
        ) : (
          name
        )}
        {!owner.active && ' (inactive)'}
      </span>
    </>
  );
}

function ArtifactRow({ artifact }: { artifact: ArtifactListItem }) {
  const to = `/artifacts/${encodeURIComponent(artifact.id)}`;
  const published = artifact.published_state === 'PUBLISHED';
  return (
    <tr>
      <td>
        <Link className="cell-link" to={to}>
          {artifact.title}
        </Link>
        {artifact.description && <span className="sub">{artifact.description}</span>}
      </td>
      <td>
        <HangsOff owner={artifact.owner} />
      </td>
      <td>
        <span className="badge badge-chip">{typeLabel(artifact.content_type)}</span>
      </td>
      <td className="num">{artifact.version_count}</td>
      <td className={artifact.published_version ? 'mono' : 'hint'}>
        {artifact.published_version ? `v${artifact.published_version.version_number}` : '—'}
      </td>
      <td>{formatDate(artifact.latest_version?.created_at ?? artifact.created_at)}</td>
      <td className="num" title="Access codes that are Active (f15.4)">
        {artifact.active_password_count}
      </td>
      <td>
        <span className={`badge ${published ? 'pill-success' : 'pill-neutral'}`}>
          {published ? 'Published' : 'Private'}
        </span>
      </td>
      <td>
        <div className="row-actions">
          <Link className="btn btn-secondary btn-icon" to={to} title="Open artifact" aria-label={`Open ${artifact.title}`}>
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

function EmptyCard({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className="e-icon">{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ArtifactsPage() {
  const list = useArtifacts();
  const levels = usePublishableLevels();
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState<ArtifactFilters>(NO_FILTERS);
  const [page, setPage] = useState(1);

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const matching = useMemo(() => filterArtifacts(items, filters), [items, filters]);
  const current = useMemo(() => artifactPageOf(matching, page), [matching, page]);

  useEffect(() => {
    if (current.page !== page) setPage(current.page);
  }, [current.page, page]);

  function setFilter<K extends keyof ArtifactFilters>(key: K, value: ArtifactFilters[K]) {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPage(1);
  }

  const filtered = filters.search !== '' || filters.level !== '' || filters.state !== '';

  return (
    <>
      <PageHead count={list.data ? items.length : null} onNew={levels.length > 0 ? () => setCreating(true) : null} />

      <div className="alert alert-neutral mb-4">
        <GitBranch aria-hidden="true" />
        <div>
          <div className="a-title">You see an artifact if you own the node it hangs off, or anything above it</div>
          <div className="a-body">
            An artifact on a Business Unit Group is visible to everyone who owns that group, any unit inside it, and
            any client below those &mdash; the ownership walk-down, nothing else.
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={filters.search}
            placeholder="Search title, description or owner"
            aria-label="Search title, description or owner"
            onChange={(event) => setFilter('search', event.target.value)}
          />
        </div>
        <select
          className="toolbar-select"
          aria-label="Filter by level"
          value={filters.level}
          onChange={(event) => setFilter('level', event.target.value as LevelFilter)}
        >
          <option value="">All levels</option>
          {LEVELS.map((level) => (
            <option key={level} value={level}>
              {levelLabel(level)}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by state"
          value={filters.state}
          onChange={(event) => setFilter('state', event.target.value as StateFilter)}
        >
          <option value="">All states</option>
          <option value="PUBLISHED">Published</option>
          <option value="PRIVATE">Private</option>
        </select>
        <div className="spacer" />
      </div>

      {list.isPending ? (
        <div className="card">
          <div className="card-body" aria-busy="true" aria-label="Loading artifacts">
            {[0, 1, 2, 3].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        </div>
      ) : list.error ? (
        <EmptyCard icon={<FileText aria-hidden="true" />} title="Artifacts could not be loaded">
          <p>{list.error instanceof Error ? list.error.message : 'Please try again.'}</p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void list.refetch()}>
            Retry
          </button>
        </EmptyCard>
      ) : items.length === 0 ? (
        <EmptyCard icon={<FileText aria-hidden="true" />} title="No artifacts yet">
          <p>Reports published on any node you own, or on anything below it, appear here.</p>
        </EmptyCard>
      ) : matching.length === 0 ? (
        <EmptyCard icon={<SearchX aria-hidden="true" />} title="No artifacts match these filters">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setFilters(NO_FILTERS);
              setPage(1);
            }}
          >
            Clear filters
          </button>
        </EmptyCard>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="data zebra">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Hangs off</th>
                  <th>Type</th>
                  <th className="num">Versions</th>
                  <th>Published version</th>
                  <th>Last upload</th>
                  <th className="num">Codes</th>
                  <th>State</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {current.rows.map((artifact) => (
                  <ArtifactRow key={artifact.id} artifact={artifact} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {items.length > 0 && matching.length > 0 && (
        <div className="pagination">
          <span className="info">
            {`Showing ${current.from}–${current.to} of ${current.total} ${current.total === 1 ? 'artifact' : 'artifacts'}`}
            {filtered ? ' matching' : ''}
          </span>
          {current.pageCount > 1 && (
            <div className="pg-btns">
              <button
                type="button"
                className="pg-btn"
                aria-label="Previous page"
                disabled={current.page === 1}
                onClick={() => setPage(current.page - 1)}
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              {Array.from({ length: current.pageCount }, (_unused, index) => index + 1).map((number) => (
                <button
                  type="button"
                  key={number}
                  className={number === current.page ? 'pg-btn is-active' : 'pg-btn'}
                  aria-label={`Page ${number}`}
                  aria-current={number === current.page ? 'page' : undefined}
                  onClick={() => setPage(number)}
                >
                  {number}
                </button>
              ))}
              <button
                type="button"
                className="pg-btn"
                aria-label="Next page"
                disabled={current.page === current.pageCount}
                onClick={() => setPage(current.page + 1)}
              >
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      )}

      {creating && (
        <NewArtifactDialog
          levels={levels}
          onClose={() => setCreating(false)}
          onCreated={() => {
            // The create invalidates the list, which refetches with the new row at the top.
            setCreating(false);
            setFilters(NO_FILTERS);
            setPage(1);
          }}
        />
      )}
    </>
  );
}
